<template>
  <div class="assign-root">
    <transition name="assign-fade" mode="out-in">
      <!-- Loading State -->
      <div v-if="loading" key="loading" class="assign-center">
        <div class="assign-spinner-wrap">
          <v-progress-circular :size="80" :width="5" color="green accent-3" indeterminate></v-progress-circular>
          <div class="assign-spinner-pulse"></div>
        </div>
        <h2 class="assign-title mt-8">{{ currentStatus }}</h2>
        <p class="assign-subtitle mt-2">Das kann einen Moment dauern…</p>
        <div class="assign-steps mt-6">
          <div v-for="(s, i) in statuses" :key="i" class="assign-step-item" :class="{ active: currentStatus === s, done: statusIndex > i }">
            <v-icon x-small :color="statusIndex > i ? '#66BB6A' : currentStatus === s ? 'white' : 'rgba(255,255,255,0.15)'">
              {{ statusIndex > i ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>{{ s }}</span>
          </div>
        </div>
      </div>

      <!-- Start State (only if not auto-started) -->
      <div v-else-if="!finished && !autoStarted" key="start" class="assign-center" style="max-width: 800px; width: 100%">
        <h2 class="assign-title mb-2">Bereit für die Magie?</h2>
        <p class="assign-subtitle mb-8" style="max-width: 500px">
          Wähle zwischen der vollautomatischen Generierung für den gesamten Monat oder der wochenweisen, interaktiven Zuweisung.
        </p>

        <v-row dense justify="center" class="w-100">
          <!-- Option A: Magie-Modus -->
          <v-col cols="12" sm="6" class="px-3">
            <v-card
              outlined
              dark
              class="grey darken-3 rounded-xl pa-5 hover-card d-flex flex-column fill-height"
              style="border: 1px solid rgba(255, 255, 255, 0.08); transition: all 0.2s">
              <div class="assign-icon-wrap mx-auto mb-4">
                <v-icon size="40" color="green accent-3">mdi-auto-fix</v-icon>
              </div>
              <h3 class="text-h6 font-weight-bold white--text mb-2">Magischer Planer</h3>
              <p class="text-caption grey--text text--lighten-1 flex-grow-1">
                Der Algorithmus erstellt den kompletten Monatsplan vollautomatisch. Er optimiert Stunden, prüft alle Abwesenheiten und stellt eine
                faire Verteilung sicher.
              </p>
              <v-btn depressed large class="assign-start-btn w-100 mt-4" @click="$emit('trigger-assignment')">
                <v-icon left>mdi-flash</v-icon> Automatisch planen
              </v-btn>
            </v-card>
          </v-col>

          <!-- Option B: Zuweisungs-Assistent -->
          <v-col cols="12" sm="6" class="px-3">
            <v-card
              outlined
              dark
              class="grey darken-3 rounded-xl pa-5 hover-card d-flex flex-column fill-height"
              style="border: 1px solid rgba(255, 255, 255, 0.08); transition: all 0.2s">
              <div class="assign-icon-wrap mx-auto mb-4" style="background: rgba(33, 150, 243, 0.1); border-color: rgba(33, 150, 243, 0.2)">
                <v-icon size="40" color="blue accent-2">mdi-account-search</v-icon>
              </div>
              <h3 class="text-h6 font-weight-bold white--text mb-2">Zuweisungs-Assistent</h3>
              <p class="text-caption grey--text text--lighten-1 flex-grow-1">
                Plane Woche für Woche interaktiv selbst. Der Assistent zeigt dir für jede Schicht den besten Kandidaten und ermöglicht gezielte
                manuelle Ausnahmen (Overrides).
              </p>
              <v-btn depressed large class="assign-view-btn w-100 mt-4" @click="$emit('start-assistant')">
                <v-icon left>mdi-gesture-double-tap</v-icon> Assistent starten
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Finished State -->
      <div v-else-if="finished" key="done" class="assign-center">
        <div class="assign-success-icon">
          <v-icon size="56" color="white">mdi-check-decagram</v-icon>
        </div>
        <h2 class="assign-title mt-6">Plan erstellt!</h2>
        <p class="assign-subtitle mt-2">Schichten generiert & Mitarbeiter zugewiesen.</p>
        <v-btn depressed large class="assign-view-btn mt-8" @click="$emit('view-plan')">
          <v-icon left>mdi-calendar-check</v-icon> Einsatzplan ansehen
        </v-btn>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ShiftManagementAssignment",
  props: {
    loading: { type: Boolean, default: false },
    finished: { type: Boolean, default: false },
    autoStart: { type: Boolean, default: false },
  },
  data() {
    return {
      currentStatus: "Initialisiere Planung...",
      statusInterval: null,
      statusIndex: 0,
      autoStarted: false,
      statuses: [
        "Sammle Mitarbeiterdaten & Regeln…",
        "Generiere Schichtbedarf…",
        "Prüfe Ferien & Abwesenheiten…",
        "Optimiere Stundensaldi…",
        "Stelle faire Verteilung sicher…",
        "Finalisiere Zuweisungen…",
      ],
    }
  },
  mounted() {
    // Auto-Start: sofort Zuweisung starten ohne dass der User klicken muss
    if (this.autoStart && !this.loading && !this.finished) {
      this.autoStarted = true
      this.$nextTick(() => {
        this.$emit("trigger-assignment")
      })
    }
  },
  watch: {
    loading(val) {
      if (val) {
        this.startAssignmentAnimation()
      } else {
        clearInterval(this.statusInterval)
      }
    },
    finished(val) {
      // Automatisch zum Plan wechseln nach kurzem Delay
      if (val) {
        setTimeout(() => {
          this.$emit("view-plan")
        }, 1200)
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.statusInterval)
  },
  methods: {
    startAssignmentAnimation() {
      this.statusIndex = 0
      this.currentStatus = this.statuses[0]
      this.statusInterval = setInterval(() => {
        this.statusIndex = (this.statusIndex + 1) % this.statuses.length
        this.currentStatus = this.statuses[this.statusIndex]
      }, 2500)
    },
  },
}
</script>

<style scoped>
.assign-root {
  width: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.assign-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px;
}
.assign-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
}
.assign-subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.5;
}

/* Spinner */
.assign-spinner-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.assign-spinner-pulse {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(102, 187, 106, 0.1);
  animation: pulseGlow 2s ease-in-out infinite;
}
@keyframes pulseGlow {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.4);
    opacity: 0;
  }
}

/* Steps Progress */
.assign-steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}
.assign-step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}
.assign-step-item.active {
  color: white;
  font-weight: 600;
}
.assign-step-item.done {
  color: #66bb6a;
}

/* Icon wraps */
.assign-icon-wrap {
  width: 96px;
  height: 96px;
  border-radius: 24px;
  background: rgba(102, 187, 106, 0.1);
  border: 2px solid rgba(102, 187, 106, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.assign-success-icon {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: successPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
@keyframes successPop {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

/* Buttons */
.assign-start-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 32px !important;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3) !important;
}
.assign-view-btn {
  background: linear-gradient(135deg, #2196f3, #1565c0) !important;
  color: white !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 32px !important;
  box-shadow: 0 4px 20px rgba(33, 150, 243, 0.3) !important;
}

/* Transition */
.assign-fade-enter-active,
.assign-fade-leave-active {
  transition: all 0.4s ease;
}
.assign-fade-enter {
  opacity: 0;
  transform: translateY(16px);
}
.assign-fade-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}
</style>
